import React from 'react'
import classNames from 'classnames'
import { CustomDropdownItemOption, type DropDownItemOptionComponentProps, type SelectOption } from '../../../SelectDropdown/SelectDropdown'
import selectDropdownStyles from '../../../SelectDropdown/SelectDropdown.module.scss'
import { useAppSelector } from '../../../../store/hooks'
import { selectAccountId, selectAuthenticatedUser } from '../../../../store/auth/authSlice'
import { useListTemplatesQuery } from '../../../../store/templateLibrary/templateLibrary'
import type { TemplateLookup, TemplateScope } from '../../../../models/TemplateLibrary'
import { RawReferenceFallback } from './RawReferenceFallback'
import { ReferencePicker } from './ReferencePicker'
import type { ZocksReferenceWidgetProps } from './registry'

interface ReferenceOptionsSource {
  options: SelectOption[]
  loading: boolean
}

const SCOPE_SUFFIX: Record<TemplateScope, string> = {
  system: 'System',
  account: 'Account',
  enterprise: 'Enterprise',
  user: 'Yours',
}

const buildDisplayLabel = (name: string, scope: TemplateScope): React.ReactNode => (
  <>
    {name} <span className='paragraph-small text-muted'>· {SCOPE_SUFFIX[scope]}</span>
  </>
)

const isVisible = (template: TemplateLookup, layer: 'account' | 'user', currentUserId: string | undefined): boolean => {
  if (template.scope !== 'user') {
    return true
  }
  if (layer === 'account') {
    return false
  }
  return template.createdBy === currentUserId
}

const useTemplateLibraryOptions = (templateType: string, typeId: string | undefined, layer: 'account' | 'user'): ReferenceOptionsSource => {
  const accountId = useAppSelector(selectAccountId)
  const currentUserId = useAppSelector(selectAuthenticatedUser)?.id
  const { data, isLoading } = useListTemplatesQuery({ accountId, type: templateType, typeId })

  const options = (data?.data ?? [])
    .filter((template) => isVisible(template, layer, currentUserId))
    .map((template) => ({ id: template.id, label: template.name, displayLabel: buildDisplayLabel(template.name, template.scope) }))
    .sort((a, b) => a.label.localeCompare(b.label))

  return { options, loading: isLoading }
}

const TemplateDropdownItemOption: React.FC<DropDownItemOptionComponentProps> = ({
  optionLabel,
  displayLabel,
  onClick,
  disabled = false,
  deselect = false,
  className = '',
}) => {
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled) {
      return
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onClick(event as unknown as React.MouseEvent<HTMLElement>)
    }
  }

  return (
    <CustomDropdownItemOption
      role='button'
      tabIndex={disabled ? -1 : 0}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      className={classNames(className, {
        [selectDropdownStyles.selectDropdownCustomItemOptionDisabled]: disabled,
        [selectDropdownStyles.selectDropdownCustomItemOptionIsDeselect]: deselect,
      })}
    >
      {displayLabel ?? optionLabel}
    </CustomDropdownItemOption>
  )
}

interface TemplateLibraryPickerProps extends ZocksReferenceWidgetProps {
  templateType: string
  typeId: string | undefined
}

const TemplateLibraryPicker: React.FC<TemplateLibraryPickerProps> = ({ templateType, typeId, ...props }) => {
  const { options, loading } = useTemplateLibraryOptions(templateType, typeId, props.layer)
  return <ReferencePicker {...props} options={options} loading={loading} dropDownItemOptionComponent={TemplateDropdownItemOption} />
}

export const TemplateLibraryWidget: React.FC<ZocksReferenceWidgetProps> = (props) => {
  const templateType = props.config.templateType
  const typeId = typeof props.config.typeId === 'string' ? props.config.typeId : undefined

  if (typeof templateType !== 'string' || !templateType) {
    return <RawReferenceFallback {...props} />
  }

  return <TemplateLibraryPicker {...props} templateType={templateType} typeId={typeId} />
}
